import type { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import React, { useEffect } from 'react';
import ErrorComponent from '../../components/ErrorComponent';
import { MetaTagsComp } from '../../components/MetaTagsComp';
import BlogDetailPage from '../../components/BlogDetailPage';
import fetchUtil from '../../utils/fetchUtil';
import { CACHE_REVALIDATE, commonJotformScript } from '../../utils/constants';
import { extractIntroParagraphs } from '../../utils/helpers';
import Head from 'next/head';

const NewsAndPressDetail: NextPage = (props: any) => {
  if (!props.pageData || props.pageData.length === 0) {
    return <ErrorComponent />
  }
  const { meta } = props.pageData[0] ||  {};
  const slug = props.pageData[0].slug;

  const updatedMeta = {
    ...meta,
    canonicalUrl: `${process.env.NEXT_PUBLIC_BASE_URL}/blogs/${slug}`,
    jotforms: meta?.jotforms?.length === 0 ? commonJotformScript : meta?.jotforms
  };

  useEffect(() => {
    const customStyle = document.documentElement.style;
    customStyle.setProperty("--navbar-color", "#6C48F0");
    customStyle.setProperty("--navbar-menu-color", "#ffffff");
  }, []);
    
  return (
    <>
      {updatedMeta && props.pageData?.length > 0 && <MetaTagsComp  {...updatedMeta} isBlogPage={true} />}
       <BlogDetailPage {...props} category="blogs" />
    </>
  )
}


export const getStaticPaths: GetStaticPaths = async () => {
  return { 
    paths: [], 
    fallback: 'blocking'  // `blocking` means the page will be built using SSR at first request and then fallback to SSG at subsequent requests
  };
}

export const getStaticProps: GetStaticProps = async (context) => {
  const { slug } = context.params as { slug: string };

  try {
    // Parallelize data fetching for better performance
    const [pageDataResponse, categoryDataResponse] = await Promise.all([
      // Fetch blog data
      fetchUtil(
        `${process.env.NEXT_PUBLIC_API_BASE_URL}/blogs?slug=${slug}&stu_dest_categories.category_ncontains=News`,
        'GET',
        {},
        '',
        false
      ) as Promise<any[]>,
      
      // Fetch category data (we'll need to get categories first, then fetch category data)
      (async () => {
        const blogData = await fetchUtil(
          `${process.env.NEXT_PUBLIC_API_BASE_URL}/blogs?slug=${slug}&stu_dest_categories.category_ncontains=News`,
          'GET',
          {},
          '',
          false
        ) as any[];
        
        const categories = blogData?.[0]?.categories || [];
        const categoryIds = Array.isArray(categories)
          ? categories.map((category: any) => category.id)
          : [];

        if (categoryIds.length > 0) {
          const idQueryString = categoryIds.join(',');
          try {
            return await fetchUtil(
              `${process.env.NEXT_PUBLIC_API_BASE_URL}/compact/categories?ids=${idQueryString}`,
              'GET',
              {},
              '',
              false
            );
          } catch (err) {
            return null;
          }
        }
        return null;
      })()
    ]);

    let pageData = pageDataResponse || [];
    let categoryData = categoryDataResponse;

    // Blog genuinely not found: return a real 404 instead of caching a 200
    // "error" page. A 200 soft-404 gets cached as a valid page by the CDN/ISR
    // and never self-heals; a 404 is re-checked each revalidate window.
    if (pageData.length === 0) {
      return { notFound: true, revalidate: 60 };
    }

    // Process intro content once
    if (pageData.length > 0) {
      const {description, intro} = extractIntroParagraphs(pageData[0]?.description || "", 2);
      pageData[0].description = description;
      pageData[0].intro = intro;
    }

    return {
      props: {
        pageData: pageData || [],
        categoryData: categoryData || null,
      },
      revalidate: CACHE_REVALIDATE
    };
  } catch (err) {
    // Transient fetch failure (CMS down/blip): the blog likely exists, we just
    // couldn't load it. Return 404 with a short revalidate so a real page
    // recovers within ~1 min instead of being frozen for the full window.
    return { notFound: true, revalidate: 60 };
  }
};

export default NewsAndPressDetail;